import React from 'react';
import { argsToStringifiable } from '@local/finsera-core/src/helpers/embed/utils';
import { _, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import Chart from './chart';
import { useUiStyles } from '../../../hooks';
import CenteredLoader from '../../centered-loader';
import ChartError from '../../chart-error';

type PieChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  readOnly: boolean;

  alert?: {
    type: 'error' | 'warning';
    message: string;
  };

  tooltipText?: string;
  hideSeries?: string[];
  numberFormat?: string;
  exportTitle?: string;
  exportSubTitle?: string;
  download?: boolean;
  category?: string;
  setExternalEmbed?: (_data: types.analysis.EmbedChartData) => void;
  seriesOrder?: (_c: string[]) => string[];
  doughnut?: boolean;
  onSelect?: (_category: string) => void;
  hideLabelsOnActive?: boolean;
  truncateLabels?: number | undefined;
  maxSlices?: number;
};

const PieChart = React.forwardRef((props: PieChartProps, ref) => {
  const uiStyles = useUiStyles();

  const {
    id,
    data,
    fullScreen,
    readOnly,

    alert,
    tooltipText = "{category}: {value.formatNumber('#,###.##')}",
    hideSeries,
    category = 'history',
    numberFormat = '#.##',
    exportTitle,
    exportSubTitle,
    download = true,
    setExternalEmbed,
    seriesOrder = (c: string[]) => c,
    doughnut = false,
    onSelect = () => undefined,
    hideLabelsOnActive = false,
    truncateLabels = undefined,
    maxSlices = 15,
  } = props;

  const [localData, setLocalData] = React.useState<types.widgets.TableData>(null);

  React.useEffect(() => {
    const handleData = async () => {
      setLocalData(null);
      if (_.isNil(data)) return;
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (setExternalEmbed)
        setExternalEmbed({
          id,
          widgetTitle: exportTitle,
          widgetSubTitle: exportSubTitle,
          chart_type: 'pie-chart-chart-v5',
          args: argsToStringifiable({
            ...props,
            data,
          }),
        });

      let newData = [...data];
      // Handle max slices logic
      if (maxSlices && data.length > maxSlices) {
        // Sort data by value descending
        const sortedData = [...data].sort((a, b) => {
          const aValue = Object.values(_.omit(a, [category])).reduce((sum: number, val) => sum + (Number(val) || 0), 0);
          const bValue = Object.values(_.omit(b, [category])).reduce((sum: number, val) => sum + (Number(val) || 0), 0);
          return bValue - aValue;
        });

        // Take top maxSlices-1 items
        const topSlices = sortedData.slice(0, maxSlices - 1);

        // Combine remaining items into "Other"
        const otherSlices = sortedData.slice(maxSlices - 1);
        const otherValue = otherSlices.reduce((sum: number, item) => {
          const value = Object.values(_.omit(item, [category])).reduce((s: number, val) => s + (Number(val) || 0), 0);
          return sum + value;
        }, 0);

        // Create other category
        const otherItem = {
          [category]: 'Other',
          value: otherValue,
        };

        // Set data as top slices plus other
        newData = [...topSlices, otherItem];
      }

      setLocalData(newData);
    };

    handleData();
  }, [data]);

  return (
    <mui.core.Box
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateRows: `${alert ? 'auto ' : ''} 1fr`,
      }}
    >
      {alert && <ChartError readOnly={readOnly} error={alert.message} severity={alert.type} />}
      <mui.core.Box sx={uiStyles.widgetContent}>
        <mui.core.Box sx={{ height: fullScreen ? 'calc(100% - 40px)' : '100%' }}>
          {_.isNil(localData) ? (
            <CenteredLoader label="Loading widget data..." top="-40px" />
          ) : (
            <Chart
              ref={ref}
              id={id}
              data={localData}
              fullScreen={fullScreen}
              numberFormat={numberFormat}
              download={download}
              category={category}
              hideSeries={hideSeries}
              tooltipText={tooltipText}
              seriesOrder={seriesOrder}
              doughnut={doughnut}
              onSelect={onSelect}
              hideLabelsOnActive={hideLabelsOnActive}
              truncateLabels={truncateLabels}
            />
          )}
        </mui.core.Box>
      </mui.core.Box>
    </mui.core.Box>
  );
});

PieChart.displayName = 'PieChart';

export default PieChart;
